import { Injectable } from '@angular/core';
import { StorageKeys } from '../model/enums/storage-keys';

@Injectable()
export class StorageService
{
    storage: Storage;

    public constructor()
    {
        if (typeof window.sessionStorage != 'undefined')
        {
            this.storage = window.sessionStorage;
        } else
        {
            this.storage = {
                clear: () => { },
                key: (index: number) => { return ''; },
                setItem: (key: any, value: any) => { },
                getItem: (key: any) => { return null; },
                removeItem: (key: any) => { },
                length: 0
            };
        }
    }

    /**
     * Stores a value in sessionStorage with optional JSON stringification.
     * 
     * By default, values are automatically JSON stringified, making this method suitable for storing
     * objects, arrays, and primitive types. For plain string values that should be stored without
     * JSON encoding (avoiding extra quotes), set `stringify` to `false`.
     * 
     * @param key - The storage key to store the value under
     * @param value - The value to store (object, array, string, number, boolean, etc.)
     * @param stringify - Whether to JSON.stringify the value (default: true)
     *                    Set to false when storing plain strings to avoid extra quotes.
     */
    public setItem<T>(key: string, value: T, stringify: boolean = true): void
    {
        this.storage.setItem(key, stringify ? JSON.stringify(value) : value as string);
    }

    public getItem<T>(key: string): T
    {
        const value = this.storage.getItem(key);
        if (value === null)
        {
            return null;
        }

        try {
            return JSON.parse(value) as T;
        } catch {
            // Not valid JSON, return as plain string
            return value as T;
        }
    }

    public removeItem(key): void
    {
        this.storage.removeItem(key);
    }

    public clearAll(): void
    {
        this.storage.clear();
    }

    /**
     * Route guard entry point used on the Frisbii return path.
     *
     * Ensures that session data is restored correctly when the user returns
     * from an external payment provider (e.g., Frisbii) that might have opened
     * a new browser tab and cleared the tab-scoped `sessionStorage`.
     *
     * Typically invoked via Angular routing guards (see `checkout-purchase/routing.ts`,
     * route: `bekraeftelse`).
     */
    public async canActivate(): Promise<void> {
      return await this.restoreSessionStorage();
    }

    /**
     * Creates a temporary backup of all current `sessionStorage` entries in `localStorage`.
     *
     * This backup allows the session to be restored if an external partner (such as Frisbii)
     * opens a new browser tab — since `sessionStorage` is tab-scoped and would otherwise
     * be lost in that case.
     *
     * The method serializes `sessionStorage` into JSON and saves it under
     * `StorageKeys.SessionBackup` in `localStorage`.
     *
     * A short delay (via `waitForStorageCommit()`) ensures the write operation
     * is fully persisted before navigation continues.
     *
     * The backup is intended for one-time use and should be removed after restoration.
     */
    public async backupSessionStorage(): Promise<void> {
      const sessionData = JSON.stringify(sessionStorage);
      localStorage.setItem(StorageKeys.SessionBackup, sessionData);

      // Wait until localStorage write is fully committed before continuing
      await this.waitForStorageCommit();
    }

    /**
     * Restores all `sessionStorage` entries from a temporary backup stored in `localStorage`.
     *
     * This is typically used when returning from an external partner such as Frisbii,
     * where `sessionStorage` may have been cleared because a new browser tab was opened.
     *
     * If a valid JWT is already present in `sessionStorage`, the function assumes the user
     * is continuing in the same tab and clears any existing `sessionBackup` from `localStorage`.
     *
     * Otherwise, it deserializes the `sessionBackup` object from `localStorage`, restores
     * all key/value pairs into `sessionStorage`, and then removes the backup to avoid
     * stale data persisting beyond its intended use.
     */
    private async restoreSessionStorage(): Promise<void> {
      if(!this.restoreSessionIntegrityCheck()) {
        return;
      }

      const sessionBackupFromLocalStorage = localStorage.getItem(StorageKeys.SessionBackup);
      // This item indicates we are restoring session in a new tab and the previous tab can act on it. (it redirects to /kvittering)
      localStorage.setItem('tabCheck', 'true');

      try {
        const parsedData = JSON.parse(sessionBackupFromLocalStorage);

        for (const [key, value] of Object.entries(parsedData)) {
          if (typeof value === 'string') {
            sessionStorage.setItem(key, value);
          }
        }
      } catch (error) {
        // In this case the user is getting redirected to /login by following the some other steps.
        console.warn('Failed to restore session data from localStorage:', error);
      } finally {
        // Always remove backup after attempting restoration
        this.removeSessionBackup();
      }
    }

    /**
     * Helper method to determine whether session restoration is required.
     *
     * If a valid JWT already exists in `sessionStorage`, or if no backup is found in `localStorage`,
     * the method returns false (skip restoration). In that case, any existing session backup is removed to prevent stale data.
     *
     * @returns `true` if session restoration should proceed, otherwise `false`.
     */
    private restoreSessionIntegrityCheck(): boolean {
      const sessionJwt = sessionStorage.getItem(StorageKeys.JWT);
      const sessionBackupFromLocalStorage = localStorage.getItem(StorageKeys.SessionBackup);

      // In both cases, session restoration is unnecessary, so the operation removes the backup.
      if (sessionJwt || !sessionBackupFromLocalStorage) {
        console.log('JWT found in sessionStorage. We are likely in the same tab.');
        if(sessionBackupFromLocalStorage) {
          console.log('Remove localstorage session backup');
          this.removeSessionBackup();
        }
        return false;
      }
      return true;
    }

    private removeSessionBackup(): void {
      localStorage.removeItem(StorageKeys.SessionBackup);
    }
    /**
     * Waits a short moment to ensure recent local storage writes are fully committed. (This is absolutely necessary)
     *
     * Uses `requestAnimationFrame` to pause until the next browser repaint (takes about 20 ms to 30 ms),
     * falling back to a brief `setTimeout` delay when unavailable.
     * Ensures `localStorage` updates are visible before continuing (e.g., before navigation).
     */
    private async waitForStorageCommit(): Promise<void> {
      const ms = 75;
      // Prefer requestAnimationFrame for micro delay; fallback to setTimeout if unavailable or skipped
      await new Promise<void>((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          try {
            requestAnimationFrame(() => setTimeout(resolve, ms));
          } catch {
            setTimeout(resolve, ms);
          }
        } else {
          setTimeout(resolve, ms);
        }
      });
    }
}
